import React from "react";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import MentionInput from "@common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "@common/Common.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { exhaustiveGuard } from "@common/utilities.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import TagsMultiSelectStandAlone from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import { useTranslation } from "@localization/useTranslation.ts";
import { EntityTemplateField, isLegalEntityStandardField, LegalEntityStandardField, TemplateEntityType } from "./EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { LegalEntityAuditFrequency, LegalEntityAuditType } from "../legalEntity/LegalEntity.types.ts";
import { CustomFieldValue } from "../system/systemEdit/SystemEdit.types.ts";
import ResponsibleField from "./fields/ResponsibleField.tsx";
import BusinessAreaField from "./fields/BusinessAreaField.tsx";
import LegalEntityCountryField from "./fields/legalEntityFields/LegalEntityCountryField.tsx";
import LegalEntityTypesField from "./fields/legalEntityFields/LegalEntityTypesField.tsx";
import LegalEntityStatusField from "./fields/legalEntityFields/LegalEntityStatusField.tsx";
import LegalEntityApprovedField from "./fields/legalEntityFields/LegalEntityApprovedField.tsx";
import LegalEntitySubContractorsField from "./fields/legalEntityFields/LegalEntitySubContractorsField.tsx";
import LegalEntityAuditTypeField from "./fields/legalEntityFields/LegalEntityAuditTypeField.tsx";
import LegalEntityAuditFrequencyField from "./fields/legalEntityFields/LegalEntityAuditFrequencyField.tsx";
import ComplianceAreasField from "./fields/ComplianceAreasField.tsx";
import LegalEntityCertificationsField from "./fields/legalEntityFields/LegalEntityCertificationsField.tsx";
import CustomTextField from "./fields/customFields/CustomTextField.tsx";
import CustomRichTextField from "./fields/customFields/CustomRichTextField.tsx";
import CustomCountryField from "./fields/customFields/CustomCountryField.tsx";
import CustomSingleSelectField from "./fields/customFields/CustomSingleSelectField.tsx";
import CustomMultiSelectField from "./fields/customFields/CustomMultiSelectField.tsx";
import CustomDateField from "./fields/customFields/CustomDateField.tsx";
import CustomNumberField from "./fields/customFields/CustomNumberField.tsx";
import CustomAmountField from "./fields/customFields/CustomAmountField.tsx";
import CustomCheckbox from "./fields/customFields/CustomCheckbox.tsx";
import CustomLegalEntityField from "./fields/customFields/CustomLegalEntityField.tsx";
import CustomUserField from "./fields/customFields/CustomUserField.tsx";
import CustomCalculatedField from "./fields/customFields/CustomCalculatedField.tsx";
import CustomDatePeriodField from "./fields/customFields/CustomDatePeriodField.tsx";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";

// The management wizard model exposes the responsible as `responsible`, while the field renderers
// expect `responsibleId`, so template rendering gets its own model.
export interface LegalEntityFieldModel {
    id: string;
    name?: string;
    description?: string;
    referenceNumber?: string;
    note?: string;
    address?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    companyRegistrationNumber?: string;
    website?: string;
    types: Array<string>;
    isDataProcessorOnTransfers: boolean;
    isSubDataProcessorOnTransfers: boolean;
    isApproved: boolean;
    isActive: boolean;
    responsibleId?: string | null;
    businessAreaIds: Array<string>;
    subContractorIds: Array<string>;
    shouldAutoAddSubContractors: boolean;
    auditType?: LegalEntityAuditType;
    auditFrequency?: LegalEntityAuditFrequency;
    complianceAreaIds: Array<string>;
    certificationIds: Array<string>;
    entityTemplateId?: string;
    customFieldValues: Array<CustomFieldValue>;
}

// TODO: No legal entity view model returns customFieldValues yet, so values persisted through this path are
// never handed back to the renderers - the fields read empty on every load until the read side returns them.
const legalEntityCustomFieldApiPath = "legalentitymanagement";

export interface LegalEntityStandardFieldProps {
    field: EntityTemplateField;
    isLoading: boolean;
    legalEntity?: LegalEntityFieldModel;
    legalEntityId: string;
    onChange: (changeFunc: (legalEntity: LegalEntityFieldModel) => void) => Promise<void>;
    // Sub contractors persist against the entity id through their own endpoints, so the page reloads the legal
    // entity afterwards. Left out by the template editor preview, which keeps that table read only.
    onSubContractorsChanged?: () => void;
    validationErrors: Array<ValidationError>;
    isPreview: boolean;
}

export interface LegalEntityCustomFieldProps {
    legalEntityId: string;
    legalEntity?: LegalEntityFieldModel;
    field: EntityTemplateField;
    onChange: (changeFunc: (legalEntity: LegalEntityFieldModel) => void) => void;
    isPreview: boolean;
    isLoading: boolean;
    validationErrors: Array<ValidationError>;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export interface LegalEntityFieldRendererProps {
    field: EntityTemplateField;
    legalEntity?: LegalEntityFieldModel;
    isLoading: boolean;
    legalEntityId?: string;
    onChange: (changeFunc: (legalEntity: LegalEntityFieldModel) => void) => Promise<void>;
    onSubContractorsChanged?: () => void;
    validationErrors?: Array<ValidationError>;
    onCustomFieldChange: (changeFunc: (legalEntity: LegalEntityFieldModel) => void) => void;
    isPreview: boolean;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export const LegalEntityStandardFieldRenderer: React.FC<LegalEntityStandardFieldProps> = ({
    field,
    legalEntity,
    legalEntityId,
    onChange,
    onSubContractorsChanged,
    validationErrors,
    isLoading,
    isPreview,
}) => {
    const { translateString } = useTranslation();
    if (!field.standardFieldIdentifier || !isLegalEntityStandardField(field.standardFieldIdentifier)) {
        return null;
    }

    const isDisabled = field.disabled || isPreview;
    const getErrorText = (standardField: LegalEntityStandardField) => validationErrors.find((v) => v.field === LegalEntityStandardField[standardField])?.error;

    switch (field.standardFieldIdentifier) {
        case LegalEntityStandardField.LegalEntityName:
            return (
                <DotLegalTextField
                    label={translateString("name")}
                    value={legalEntity?.name ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.name = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityName)}
                />
            );
        case LegalEntityStandardField.LegalEntityDescription:
            return (
                <MentionInput
                    multiline
                    rows={6}
                    label={translateString("description")}
                    value={legalEntity?.description ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.description = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityDescription)}
                    {...(isNullOrWhitespace(legalEntityId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: legalEntityId, entityFromType: EntityType.LegalEntity } })}
                />
            );
        case LegalEntityStandardField.LegalEntityReferenceNumber:
            return (
                <DotLegalTextField
                    label={translateString("referenceNumber")}
                    value={legalEntity?.referenceNumber ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.referenceNumber = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityReferenceNumber)}
                />
            );
        case LegalEntityStandardField.LegalEntityNote:
            return (
                <DotLegalTextField
                    label={translateString("note")}
                    value={legalEntity?.note ?? ""}
                    debounce
                    multiline
                    rows={4}
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.note = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityNote)}
                />
            );
        case LegalEntityStandardField.LegalEntityAddress:
            return (
                <DotLegalTextField
                    label={translateString("address")}
                    value={legalEntity?.address ?? ""}
                    debounce
                    multiline
                    rows={3}
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.address = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityAddress)}
                />
            );
        case LegalEntityStandardField.LegalEntityZipCode:
            return (
                <DotLegalTextField
                    label={translateString("zipCode")}
                    value={legalEntity?.zipCode ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.zipCode = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityZipCode)}
                />
            );
        case LegalEntityStandardField.LegalEntityCity:
            return (
                <DotLegalTextField
                    label={translateString("city")}
                    value={legalEntity?.city ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.city = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityCity)}
                />
            );
        case LegalEntityStandardField.LegalEntityCountry:
            return (
                <LegalEntityCountryField
                    country={legalEntity?.country}
                    onChange={(country) =>
                        onChange((entity) => {
                            entity.country = country;
                        })
                    }
                    disabled={isDisabled}
                    isLoading={isLoading}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityCountry)}
                />
            );
        case LegalEntityStandardField.LegalEntityCompanyRegistrationNumber:
            return (
                <DotLegalTextField
                    label={translateString("companyRegistrationNumber")}
                    value={legalEntity?.companyRegistrationNumber ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.companyRegistrationNumber = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityCompanyRegistrationNumber)}
                />
            );
        case LegalEntityStandardField.LegalEntityWebsite:
            return (
                <DotLegalTextField
                    label={translateString("website")}
                    value={legalEntity?.website ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.website = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityWebsite)}
                />
            );
        case LegalEntityStandardField.LegalEntityTypes:
            return (
                <LegalEntityTypesField
                    legalEntityId={legalEntityId}
                    types={legalEntity?.types ?? []}
                    onChange={(types) =>
                        onChange((entity) => {
                            entity.types = types;
                        })
                    }
                    isDataProcessorOnTransfers={legalEntity?.isDataProcessorOnTransfers}
                    isSubDataProcessorOnTransfers={legalEntity?.isSubDataProcessorOnTransfers}
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityTypes)}
                />
            );
        case LegalEntityStandardField.LegalEntityApproved:
            return (
                <LegalEntityApprovedField
                    isApproved={legalEntity?.isApproved ?? false}
                    onChange={(isApproved) =>
                        onChange((entity) => {
                            entity.isApproved = isApproved;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityApproved)}
                />
            );
        case LegalEntityStandardField.LegalEntityStatus:
            return (
                <LegalEntityStatusField
                    isActive={legalEntity?.isActive ?? true}
                    onChange={(isActive) =>
                        onChange((entity) => {
                            entity.isActive = isActive;
                        })
                    }
                    disabled={isDisabled}
                    isLoading={isLoading}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityStatus)}
                />
            );
        case LegalEntityStandardField.LegalEntityBusinessAreas:
            return (
                <BusinessAreaField
                    businessAreas={legalEntity?.businessAreaIds ?? []}
                    isLoading={isLoading}
                    onChange={(businessAreaIds) =>
                        onChange((entity) => {
                            entity.businessAreaIds = businessAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityBusinessAreas)}
                />
            );
        case LegalEntityStandardField.LegalEntityTags:
            return (
                <TagsMultiSelectStandAlone
                    entityId={legalEntityId}
                    isLoading={false}
                    entityType={TagRelationType.LegalEntity}
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityTags)}
                    disableQuery={isPreview}
                />
            );
        case LegalEntityStandardField.LegalEntityResponsible:
            return (
                <ResponsibleField
                    responsibleId={legalEntity?.responsibleId}
                    isLoading={isLoading}
                    onChange={(responsibleId) =>
                        onChange((entity) => {
                            entity.responsibleId = responsibleId;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityResponsible)}
                />
            );
        case LegalEntityStandardField.LegalEntitySubContractors:
            return (
                <LegalEntitySubContractorsField
                    legalEntityId={legalEntityId}
                    legalEntityName={legalEntity?.name}
                    subContractorIds={legalEntity?.subContractorIds ?? []}
                    shouldAutoAddSubContractors={legalEntity?.shouldAutoAddSubContractors ?? false}
                    onShouldAutoAddSubContractorsChange={(shouldAutoAdd) =>
                        onChange((entity) => {
                            entity.shouldAutoAddSubContractors = shouldAutoAdd;
                        })
                    }
                    onSubContractorsChanged={onSubContractorsChanged}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntitySubContractors)}
                />
            );
        case LegalEntityStandardField.LegalEntityAuditType:
            return (
                <LegalEntityAuditTypeField
                    auditType={legalEntity?.auditType}
                    onChange={(auditType) =>
                        onChange((entity) => {
                            entity.auditType = auditType;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityAuditType)}
                />
            );
        case LegalEntityStandardField.LegalEntityAuditFrequency:
            return (
                <LegalEntityAuditFrequencyField
                    auditFrequency={legalEntity?.auditFrequency}
                    onChange={(auditFrequency) =>
                        onChange((entity) => {
                            entity.auditFrequency = auditFrequency;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityAuditFrequency)}
                />
            );
        case LegalEntityStandardField.LegalEntityComplianceAreas:
            return (
                <ComplianceAreasField
                    complianceAreaIds={legalEntity?.complianceAreaIds ?? []}
                    onChange={(complianceAreaIds) =>
                        onChange((entity) => {
                            entity.complianceAreaIds = complianceAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityComplianceAreas)}
                />
            );
        case LegalEntityStandardField.LegalEntityCertifications:
            return (
                <LegalEntityCertificationsField
                    certificationIds={legalEntity?.certificationIds ?? []}
                    onChange={(certificationIds) =>
                        onChange((entity) => {
                            entity.certificationIds = certificationIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(LegalEntityStandardField.LegalEntityCertifications)}
                />
            );
        default:
            return exhaustiveGuard(field.standardFieldIdentifier);
    }
};

export const LegalEntityCustomFieldRenderer: React.FC<LegalEntityCustomFieldProps> = ({
    legalEntity,
    legalEntityId,
    field,
    onChange,
    isPreview,
    isLoading = false,
    validationErrors,
    computedFieldEditing,
}) => {
    if (!field.customInputFieldType) {
        return null;
    }

    const isDisabled = isPreview || field.disabled;
    const value = (legalEntity?.customFieldValues ?? []).find((x) => x.customInputFieldId == field.customInputFieldId)?.value ?? "";
    const errorText = validationErrors.find((v) => v.field === field.id)?.error;

    switch (field.customInputFieldType) {
        case InputFieldTypeEnum.TextField:
            return (
                <CustomTextField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.RichTextField:
            return (
                <CustomRichTextField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CountryField:
            return (
                <CustomCountryField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.SingleDropDown:
            return (
                <CustomSingleSelectField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.MultiDropDown:
            return (
                <CustomMultiSelectField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.DateField:
            return (
                <CustomDateField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as Date | null}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.NumberField:
            return (
                <CustomNumberField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.AmountField:
            return (
                <CustomAmountField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as { Amount: number; CurrencyCode: string }}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CheckboxField:
            return (
                <CustomCheckbox
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as boolean}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.LegalEntityField:
            return (
                <CustomLegalEntityField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.UserField:
            return (
                <CustomUserField
                    isLoading={isLoading}
                    entityId={legalEntityId}
                    entityApiPath={legalEntityCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CalculatedField:
            return (
                <CustomCalculatedField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.LegalEntity}
                    computedFieldEditing={computedFieldEditing}
                    entityTemplateId={legalEntity?.entityTemplateId}
                    entity={{ customFieldValues: legalEntity?.customFieldValues ?? [] }}
                />
            );

        case InputFieldTypeEnum.DatePeriodField:
            return (
                <CustomDatePeriodField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.LegalEntity}
                    computedFieldEditing={computedFieldEditing}
                    entity={{ customFieldValues: legalEntity?.customFieldValues ?? [] }}
                />
            );
        default:
            return exhaustiveGuard(field.customInputFieldType);
    }
};

export const LegalEntityFieldRenderer: React.FC<LegalEntityFieldRendererProps> = (props) => {
    if (props.field.standardFieldIdentifier) {
        return (
            <LegalEntityStandardFieldRenderer
                field={props.field}
                legalEntity={props.legalEntity}
                legalEntityId={props.legalEntityId ?? ""}
                onChange={props.onChange}
                onSubContractorsChanged={props.onSubContractorsChanged}
                validationErrors={props.validationErrors ?? []}
                isPreview={props.isPreview}
                isLoading={props.isLoading}
            />
        );
    }

    return (
        <LegalEntityCustomFieldRenderer
            legalEntity={props.legalEntity}
            field={props.field}
            onChange={props.onCustomFieldChange}
            isPreview={props.isPreview}
            legalEntityId={props.legalEntityId ?? ""}
            isLoading={props.isLoading}
            validationErrors={props.validationErrors ?? []}
            computedFieldEditing={props.computedFieldEditing}
        />
    );
};
